import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import { X, CaretUp, CaretDown, Minus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Sparkline from "@/components/Sparkline";
import TrendBadge from "@/components/TrendBadge";

export default function WatchlistTable({ rows, isLoading, onChange, notifEnabled }) {
  const prevTrend = useRef({});

  useEffect(() => {
    if (!notifEnabled || !rows) return;
    rows.forEach((r) => {
      const key = `${r.asset_type}:${r.symbol}`;
      const trend = r.technical?.trend;
      if (!trend) return;
      const prev = prevTrend.current[key];
      if (prev && prev !== trend) {
        try {
          new Notification(`${r.display_symbol} flipped ${trend.toUpperCase()}`, {
            body: `Now ${trend} at $${r.quote?.price}`,
          });
        } catch (e) {
          // no-op
        }
      }
      prevTrend.current[key] = trend;
    });
  }, [rows, notifEnabled]);

  const remove = async (id, symbol) => {
    try {
      await api.removeFromWatchlist(id);
      toast.success(`${symbol} removed`);
      onChange?.();
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  return (
    <div className="bg-[#0a0a0a]" data-testid="watchlist-table">
      <div className="grid grid-cols-[minmax(120px,1.4fr)_1fr_1fr_1fr_120px_44px] items-center px-4 h-10 border-b border-zinc-800 overline text-zinc-500">
        <div>Ticker</div>
        <div className="text-right">Price</div>
        <div className="text-right">24h %</div>
        <div className="text-right">Signal</div>
        <div className="text-right">Trend</div>
        <div></div>
      </div>

      {isLoading && (
        <div className="p-8 text-center text-zinc-500 text-sm">Loading watchlist…</div>
      )}

      {!isLoading && rows.length === 0 && (
        <div className="p-8 text-center text-zinc-500 text-sm">
          Your watchlist is empty. Click <span className="text-white font-bold">ADD TICKER</span> to get started.
        </div>
      )}

      {rows.map((r) => {
        const up = (r.quote?.change_pct ?? 0) >= 0;
        return (
          <Link
            to={`/ticker/${r.asset_type}/${encodeURIComponent(r.symbol)}`}
            key={r.id}
            data-testid={`watchlist-row-${r.display_symbol}`}
            className="grid grid-cols-[minmax(120px,1.4fr)_1fr_1fr_1fr_120px_44px] items-center px-4 h-16 border-b border-zinc-900 hover:bg-[#141414] transition-colors group"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex flex-col leading-tight min-w-0">
                <span className="font-bold text-white truncate">
                  {r.display_symbol}
                  <span className="ml-2 overline text-[9px] text-zinc-600">
                    {r.asset_type}
                  </span>
                </span>
                <span className="text-xs text-zinc-500 truncate">{r.name}</span>
              </div>
            </div>

            <div className="text-right tabular text-white">
              {r.quote ? `$${fmt(r.quote.price)}` : <span className="text-zinc-600">—</span>}
            </div>

            <div className={`text-right tabular flex items-center justify-end gap-1 ${
              r.quote == null ? "text-zinc-600" :
              up ? "text-[#00FF66]" : "text-[#FF3B30]"
            }`}>
              {r.quote != null && (up ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
              {r.quote != null ? `${Math.abs(r.quote.change_pct).toFixed(2)}%` : "—"}
            </div>

            <div className="flex items-center justify-end gap-3">
              <Sparkline
                points={r.sparkline || []}
                positive={up}
              />
            </div>

            <div className="text-right">
              <TrendBadge trend={r.technical?.trend} confidence={r.technical?.confidence} />
            </div>

            <div className="flex justify-end">
              <button
                data-testid={`remove-ticker-${r.display_symbol}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  remove(r.id, r.display_symbol);
                }}
                className="w-8 h-8 border border-transparent hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-600 flex items-center justify-center transition-colors"
                aria-label={`Remove ${r.display_symbol}`}
              >
                <X size={12} weight="bold" />
              </button>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function fmt(n) {
  if (n == null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (abs >= 1) return n.toFixed(2);
  return n.toFixed(6);
}
